import React, {useState} from "react";
import styled from "styled-components";
import {Link} from "react-router-dom";
import {BoardContainer, TitleContainer} from "../../assets/GlobalStyles";

const API_DOMAIN = "https://dsp.oaple.co.kr";
const API_PATH = "/audio/ad";
const DEFAULT_MEDIA_SCRIPT_NO = "49003821-554a-437a-b1eb-1512a40570d1";

function buildRequestUrl(mediaScriptNo, output) {
  const params = new URLSearchParams({mediaScriptNo, output});
  return `${API_DOMAIN}${API_PATH}?${params.toString()}`;
}

function formatBody(text, output) {
  if (output === "json") {
    try {
      return JSON.stringify(JSON.parse(text), null, 2);
    } catch (e) {
      return text;
    }
  }
  return text;
}

export default function ApiGuideTest() {
  const [mediaScriptNo, setMediaScriptNo] = useState(DEFAULT_MEDIA_SCRIPT_NO);
  const [output, setOutput] = useState("xml");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const trimmedMediaScriptNo = mediaScriptNo.trim();
  const requestUrl = buildRequestUrl(trimmedMediaScriptNo, output);

  const sendRequest = async () => {
    if (!trimmedMediaScriptNo) {
      setError("mediaScriptNo를 입력해주세요.");
      setResult(null);
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const response = await fetch(requestUrl);
      const text = await response.text();
      setResult({
        status: response.status,
        ok: response.ok,
        contentType: response.headers.get("content-type") || "-",
        body: formatBody(text, output),
      });
    } catch (e) {
      setError("요청을 보내지 못했습니다. 브라우저의 CORS 정책으로 응답이 차단되었을 수 있습니다. 아래 '새 창에서 열기'로 직접 확인해보세요.");
    } finally {
      setLoading(false);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(requestUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      setCopied(false);
    }
  };

  return (
    <main>
      <BoardContainer>
        <TitleContainer>
          <div>
            <h1>API 테스트</h1>
          </div>
          <GuideLink to="/board/apiGuide">API 가이드 보기 ↗</GuideLink>
        </TitleContainer>

        <Lead>API 가이드에서 안내한 광고 송출 API에 실제로 요청을 보내 응답을 확인할 수 있습니다.</Lead>

        <Panel>
          <FieldRow>
            <FieldLabel htmlFor="mediaScriptNo">mediaScriptNo</FieldLabel>
            <TextInput
              id="mediaScriptNo"
              value={mediaScriptNo}
              onChange={e => setMediaScriptNo(e.target.value)}
              placeholder="지면 번호를 입력하세요"
            />
          </FieldRow>

          <FieldRow>
            <FieldLabel>output</FieldLabel>
            <OutputToggle>
              <ToggleButton type="button" $active={output === "xml"} onClick={() => setOutput("xml")}>xml</ToggleButton>
              <ToggleButton type="button" $active={output === "json"} onClick={() => setOutput("json")}>json</ToggleButton>
            </OutputToggle>
          </FieldRow>

          <RequestUrlRow>
            <RequestUrlLabel>요청 URL</RequestUrlLabel>
            <RequestUrlValue>{requestUrl}</RequestUrlValue>
            <SmallButton type="button" onClick={copyUrl}>{copied ? "복사됨" : "복사"}</SmallButton>
            <SmallButton as="a" href={requestUrl} target="_blank" rel="noopener noreferrer">새 창에서 열기</SmallButton>
          </RequestUrlRow>

          <ActionRow>
            <SendButton type="button" onClick={sendRequest} disabled={loading}>
              {loading ? "요청 중…" : "요청 보내기"}
            </SendButton>
          </ActionRow>

          {error && <ErrorBox>{error}</ErrorBox>}

          {result && (
            <ResultBlock>
              <ResultHeader>
                <StatusBadge $ok={result.ok}>{result.status}</StatusBadge>
                <span>{result.contentType}</span>
              </ResultHeader>
              <pre>{result.body}</pre>
            </ResultBlock>
          )}
        </Panel>
      </BoardContainer>
    </main>
  );
}

const GuideLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 14px;
  border: 1px solid var(--color-primary-border);
  border-radius: 8px;
  background: var(--color-primary-soft);
  color: var(--color-primary-strong);
  font-size: var(--font-size-13);
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: var(--color-primary-muted);
    border-color: var(--color-primary-focus);
  }
`;

const Lead = styled.p`
  margin: 4px 0 20px;
  color: #666e76;
  font-size: var(--font-size-14);
  line-height: 1.6;
`;

const Panel = styled.section`
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-surface);
  box-shadow: 0 5px 18px rgba(31, 41, 55, .045);
  padding: 20px;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 14px;
`;

const FieldLabel = styled.label`
  min-width: 92px;
  color: var(--color-text);
  font-size: var(--font-size-14);
  font-weight: 700;
`;

const TextInput = styled.input`
  flex: 1;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #dde3e9;
  border-radius: 7px;
  color: var(--color-text-strong);
  font-size: var(--font-size-14);

  &:focus {
    border-color: var(--color-primary-focus);
    outline: none;
  }
`;

const OutputToggle = styled.div`
  display: inline-flex;
  border: 1px solid #dde3e9;
  border-radius: 7px;
  overflow: hidden;
`;

const ToggleButton = styled.button`
  min-width: 68px;
  height: 38px;
  border: 0;
  background: ${p => (p.$active ? "var(--color-primary-strong)" : "var(--color-surface)")};
  color: ${p => (p.$active ? "var(--color-surface)" : "#5f6770")};
  font-size: var(--font-size-14);
  font-weight: 700;

  &:hover {
    background: ${p => (p.$active ? "var(--color-primary-strong)" : "#f3f5f7")};
  }
`;

const RequestUrlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 16px 0;
  padding: 10px 12px;
  border: 1px solid #e4e9ee;
  border-radius: 8px;
  background: #f7f9fb;
`;

const RequestUrlLabel = styled.span`
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-12);
  font-weight: 700;
`;

const RequestUrlValue = styled.code`
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  color: var(--color-text-strong);
  font-size: var(--font-size-13);
  white-space: nowrap;
`;

const SmallButton = styled.button`
  flex-shrink: 0;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #dde3e9;
  border-radius: 6px;
  background: var(--color-surface);
  color: var(--color-text);
  font-size: var(--font-size-12);
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
  align-content: center;

  &:hover {
    border-color: var(--color-primary-focus);
    background: var(--color-primary-soft);
    color: var(--color-primary-strong);
  }
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SendButton = styled.button`
  height: 40px;
  padding: 0 22px;
  border: 0;
  border-radius: 8px;
  background: var(--color-primary-strong);
  color: var(--color-surface);
  font-size: var(--font-size-14);
  font-weight: 750;

  &:hover {
    background: #1d4fd1;
  }

  &:disabled {
    background: #9db8ef;
    cursor: not-allowed;
  }
`;

const ErrorBox = styled.div`
  margin-top: 16px;
  padding: 10px 14px;
  border: 1px solid #fecaca;
  border-radius: 7px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: var(--font-size-13);
  line-height: 1.6;
`;

const ResultBlock = styled.div`
  margin-top: 16px;
  overflow: hidden;
  border: 1px solid #e3e8ee;
  border-radius: 8px;

  pre {
    margin: 0;
    padding: 14px 16px;
    overflow-x: auto;
    background: #1e2530;
    color: #d7e0ea;
    font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
    font-size: var(--font-size-12-5);
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-all;
  }
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  background: #eef2f6;
  color: #4b5563;
  font-size: var(--font-size-12);
  font-weight: 700;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${p => (p.$ok ? "var(--color-primary-soft)" : "#fef2f2")};
  color: ${p => (p.$ok ? "var(--color-primary-strong)" : "#b91c1c")};
  font-size: var(--font-size-11);
  font-weight: 750;
`;
